'use client';

import { useTranslations } from 'next-intl';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';

interface LanguageFilterProps {
  /** Selected language code, or '' for "all". */
  value: string;
  onChange: (value: string) => void;
  /** Language codes actually present in the current data set. */
  options: string[];
  /** Base test id; options get `${testId}-<code>` and `${testId}-all`. */
  testId?: string;
}

/**
 * Shared language filter used by every library grid (stories, audio-stories,
 * books). Renders only when the data has at least one language, so single-language
 * families don't see a dead control — but a filter is always available the moment a
 * second language appears, consistently across all grids.
 */
export function LanguageFilter({ value, onChange, options, testId }: LanguageFilterProps) {
  const t = useTranslations('common');
  // Render whenever the data has a language, so the filter is consistently available
  // across grids (even single-language families expect the control to be there).
  if (options.length === 0) return null;

  return (
    <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
      <SelectTrigger className="w-32 sm:w-40" data-testid={testId} aria-label={t('allLanguages')}>
        <SelectValue placeholder={t('allLanguages')} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid={testId ? `${testId}-all` : undefined}>
          {t('allLanguages')}
        </SelectItem>
        {options.map((lang) => (
          <SelectItem
            key={lang}
            value={lang}
            data-testid={testId ? `${testId}-${lang}` : undefined}
          >
            {lang.toUpperCase()}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
